import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faImage,
  faCamera,
  faTrashAlt,
  faInfoCircle,
  faSpinner,
  faCheck,
  faExclamationTriangle,
  faStore
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../contexts/LanguageContext';
import { uploadDairyLogo, clearDairyLogo } from '../services/api';
import { validateLogoFile, logoApiErrorKey } from '../utils/logoValidation';
import ConfirmationModal from './ConfirmationModal';
import './DairyLogoModal.css';

// Modal for viewing, uploading, replacing, and removing the dairy logo.
// The logo is stored on the DairyInformation record and, when present, is
// printed in the top-left corner of every generated PDF report.
const DairyLogoModal = ({ isOpen, onClose, dairyInfo, onDairyInfoChange }) => {
  const { t } = useLanguage();
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  // The modal is only reachable from the profile dropdown's dairy row, which
  // itself requires dairyInfo — guard anyway so a stale open can't throw on
  // dairyInfo.id during upload/remove.
  if (!isOpen || !dairyInfo) return null;

  const dairyName = dairyInfo?.dairy_name || '';
  const currentLogo = dairyInfo?.logo || null;

  const resetFeedback = () => setFeedback({ type: '', message: '' });

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];

    // Reset the input so picking the same (e.g. rejected) file again re-fires change
    event.target.value = '';

    if (!file) return;

    const validation = validateLogoFile(file);
    if (!validation.valid) {
      setFeedback({ type: 'error', message: t(validation.errorKey) });
      return;
    }

    try {
      setIsUploading(true);
      resetFeedback();

      const updatedDairyInfo = await uploadDairyLogo(dairyInfo.id, file, dairyName);

      if (onDairyInfoChange) {
        onDairyInfoChange(updatedDairyInfo);
      }

      setFeedback({ type: 'success', message: t('logoUpdatedSuccessfully') });
    } catch (error) {
      const errorKey = logoApiErrorKey(error);
      setFeedback({
        type: 'error',
        message: errorKey ? t(errorKey) : t('logoUploadFailed')
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveConfirmed = async () => {
    setShowRemoveConfirm(false);

    try {
      setIsRemoving(true);
      resetFeedback();

      const updatedDairyInfo = await clearDairyLogo(dairyInfo.id, dairyName);

      if (onDairyInfoChange) {
        onDairyInfoChange(updatedDairyInfo);
      }

      setFeedback({ type: 'success', message: t('logoRemovedSuccessfully') });
    } catch (error) {
      setFeedback({ type: 'error', message: t('logoRemoveFailed') });
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <>
      <div className="dlm-modal-overlay" onClick={onClose}>
        <div className="dlm-modal-container" onClick={(e) => e.stopPropagation()}>
          <div className="dlm-modal-header">
            <h3 className="dlm-modal-title">{t('dairyLogo')}</h3>
            <button
              className="dlm-modal-close-btn"
              onClick={onClose}
              aria-label={t('close')}
              disabled={isUploading || isRemoving}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className="dlm-modal-body">
            {feedback.message && (
              <div className={`dlm-feedback-banner ${feedback.type}`}>
                <FontAwesomeIcon
                  icon={feedback.type === 'success' ? faCheck : faExclamationTriangle}
                  className="dlm-feedback-icon"
                />
                <span>{feedback.message}</span>
              </div>
            )}

            {/* Current logo preview */}
            <div className="dlm-logo-preview-area">
              {isUploading || isRemoving ? (
                <div className="dlm-logo-loading">
                  <FontAwesomeIcon icon={faSpinner} spin />
                </div>
              ) : currentLogo ? (
                <img
                  src={currentLogo}
                  alt={dairyName || t('dairyLogo')}
                  className="dlm-logo-image"
                />
              ) : (
                <div className="dlm-logo-placeholder">
                  <FontAwesomeIcon icon={faImage} className="dlm-logo-placeholder-icon" />
                  <span>{t('logoNotSet')}</span>
                </div>
              )}
            </div>

            {/* Dairy name caption */}
            {dairyName && (
              <div className="dlm-dairy-name-caption">
                <FontAwesomeIcon icon={faStore} className="dlm-caption-icon" />
                <span>{dairyName}</span>
              </div>
            )}

            {/* Actions */}
            <div className="dlm-actions-row">
              <button
                type="button"
                className="dlm-upload-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || isRemoving}
              >
                <FontAwesomeIcon icon={faCamera} />
                <span>{currentLogo ? t('logoReplace') : t('logoUpload')}</span>
              </button>

              {currentLogo && (
                <button
                  type="button"
                  className="dlm-remove-btn"
                  onClick={() => setShowRemoveConfirm(true)}
                  disabled={isUploading || isRemoving}
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                  <span>{t('logoRemove')}</span>
                </button>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png, image/jpeg, image/webp"
              onChange={handleFileSelected}
              className="dlm-hidden-file-input"
              aria-label={t('logoUpload')}
            />

            {/* Hint */}
            <div className="dlm-hint-box">
              <FontAwesomeIcon icon={faInfoCircle} className="dlm-hint-icon" />
              <span>{t('logoHint')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Remove confirmation */}
      <ConfirmationModal
        isOpen={showRemoveConfirm}
        onClose={() => setShowRemoveConfirm(false)}
        onConfirm={handleRemoveConfirmed}
        title={t('logoRemove')}
        message={t('logoRemoveConfirmMessage')}
        fieldValue={dairyName}
      />
    </>
  );
};

export default DairyLogoModal;
